import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Package, Eye, ShoppingCart, CreditCard, DollarSign, Loader2, ChevronUp, ChevronDown } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

const API_URL = config.API_URL;

/**
 * GA4EcommerceProducts - Affiche les achats e-commerce par article
 * Colonnes: Nom de l'article, Articles consultés, Articles ajoutés au panier, Articles achetés,
 * Chiffre d'affaires
 */
const GA4EcommerceProducts = ({
  token,
  propertyId,
  period,
  customStartDate,
  customEndDate,
  formatNumber,
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_ecommerce_products'
}) => {
  const [productsData, setProductsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'viewed', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [groupBy, setGroupBy] = useState('itemName'); // 'itemName' ou 'itemCategory'

  const itemsPerPage = 10;

  useEffect(() => {
    if (propertyId) {
      loadProductsData();
    }
  }, [propertyId, period, customStartDate, customEndDate, groupBy]);

  // Une réponse lente d'une période abandonnée ne doit pas écraser la période courante
  const abortRef = useRef(null);

  const loadProductsData = async () => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/dashboard/ga4/ecommerce-products`,
        {
          propertyId,
          period,
          customStartDate,
          customEndDate,
          groupBy
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        }
      );

      if (response.data.success) {
        setProductsData(response.data.data);
        setCurrentPage(1); // Reset à la première page
      } else {
        setError(response.data.error || 'Erreur de chargement');
      }
    } catch (err) {
      // Requête remplacée par une plus récente : ne rien écraser
      if (err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('[GA4-PRODUCTS] Erreur:', err);
      setError(err.response?.data?.error || 'Erreur de chargement des produits');
    } finally {
      if (abortRef.current === controller) {
        setLoading(false);
      }
    }
  };

  // Tri des produits
  const sortedProducts = useMemo(() => {
    if (!productsData?.products) return [];

    const sorted = [...productsData.products].sort((a, b) => {
      let aValue, bValue;

      switch (sortConfig.key) {
        case 'itemName':
          aValue = a.itemName.toLowerCase();
          bValue = b.itemName.toLowerCase();
          break;
        case 'viewed':
          aValue = a.viewed;
          bValue = b.viewed;
          break;
        case 'addedToCart':
          aValue = a.addedToCart;
          bValue = b.addedToCart;
          break;
        case 'purchased':
          aValue = a.purchased;
          bValue = b.purchased;
          break;
        case 'revenue':
        default:
          aValue = a.revenue;
          bValue = b.revenue;
          break;
      }

      if (sortConfig.key === 'itemName') {
        return sortConfig.direction === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });

    return sorted;
  }, [productsData?.products, sortConfig]);

  // Pagination
  const totalProducts = sortedProducts.length;
  const totalPages = Math.ceil(totalProducts / itemsPerPage);

  // Réinitialiser la page quand les données changent
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  // Données paginées
  const paginatedProducts = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);
    return sortedProducts.slice(startIndex, endIndex);
  }, [sortedProducts, currentPage, itemsPerPage, totalProducts]);

  const handleSort = useCallback((key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
    }));
    setCurrentPage(1); // Réinitialiser à la première page
  }, []);

  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(Math.max(1, Math.min(totalPages, newPage)));
  }, [totalPages]);

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronDown size={14} style={{ opacity: 0.3 }} />;
    }
    return sortConfig.direction === 'desc'
      ? <ChevronDown size={14} />
      : <ChevronUp size={14} />;
  };

  if (loading) {
    return (
      <div style={{
        marginTop: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        padding: '40px',
        textAlign: 'center'
      }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#6b7280' }} />
        <p style={{ marginTop: '12px', color: '#6b7280' }}>Chargement des produits e-commerce...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        marginTop: '24px',
        backgroundColor: '#fef2f2',
        borderRadius: '12px',
        border: '1px solid #fecaca',
        padding: '20px',
        color: '#dc2626'
      }}>
        {error}
      </div>
    );
  }

  if (!productsData || !productsData.products || productsData.products.length === 0) {
    /**
     * Un bloc vide se montre, et dit pourquoi.
     *
     * Il ne le faisait que pour le regroupement par catégorie ; par nom d'article — le
     * regroupement **par défaut** — il rendait `null` et disparaissait de la page sans un mot.
     * Un composant qui s'évanouit est indiscernable d'un composant en panne : on cherche une
     * erreur de chargement là où GA4 n'a simplement rien à donner.
     *
     * La cause est presque toujours la même, et elle se corrige dans le tracking, pas ici :
     * les événements `purchase` remontent sans tableau `items`, donc sans article à lister. Le
     * dire épargne de chercher ailleurs.
     */
    return (
      <div style={{
        marginTop: '24px',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <h3 style={{
              margin: 0,
              fontSize: '1.1em',
              fontWeight: '600',
              color: '#1f2937',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Package size={20} style={{ color: '#1a1a1a' }} />
              Achats e-commerce
            </h3>
            {/* Le sélecteur reste actif : l'autre regroupement peut, lui, porter des données. */}
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: '#ffffff',
                color: '#374151',
                cursor: 'pointer'
              }}
            >
              <option value="itemName">Nom de l'article</option>
              <option value="itemCategory">Catégorie de l'article</option>
            </select>
          </div>
        </div>
        <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
          <p style={{ margin: 0 }}>
            {groupBy === 'itemCategory'
              ? "Aucune catégorie d'article sur cette période."
              : "Aucun article sur cette période."}
            <br />
            <span style={{ fontSize: '0.9em' }}>
              Google Analytics ne renvoie pas de données d'articles : vérifiez que vos événements
              d'achat envoient bien le tableau <code>items</code>.
            </span>
          </p>
        </div>
      </div>
    );
  }

  const { totals } = productsData;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);

  return (
    <div className="ga4-ecommerce-products" style={{
      marginTop: '24px',
      backgroundColor: '#ffffff',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid #e5e7eb',
        backgroundColor: '#f9fafb',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <h3 style={{
            margin: 0,
            fontSize: '1.1em',
            fontWeight: '600',
            color: '#1f2937',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Package size={20} style={{ color: '#1a1a1a' }} />
            Achats e-commerce
          </h3>

          {/* Dropdown pour choisir le groupement */}
          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            style={{
              padding: '6px 12px',
              fontSize: '0.85em',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              backgroundColor: '#ffffff',
              color: '#374151',
              cursor: 'pointer'
            }}
          >
            <option value="itemName">Nom de l'article</option>
            <option value="itemCategory">Catégorie de l'article</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Totaux */}
          <div style={{
            display: 'flex',
            gap: '24px',
            fontSize: '0.875rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <Eye size={16} style={{ color: '#6b7280' }} />
              <span style={{ color: '#6b7280' }}>Consultés:</span>
              <strong style={{ color: '#1f2937' }}>
                {formatNumber ? formatNumber(totals.totalViewed) : totals.totalViewed.toLocaleString()}
              </strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <ShoppingCart size={16} style={{ color: '#6b7280' }} />
              <span style={{ color: '#6b7280' }}>Au panier:</span>
              <strong style={{ color: '#1f2937' }}>
                {formatNumber ? formatNumber(totals.totalAddedToCart) : totals.totalAddedToCart.toLocaleString()}
              </strong>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <CreditCard size={16} style={{ color: '#6b7280' }} />
              <span style={{ color: '#6b7280' }}>Achetés:</span>
              <strong style={{ color: '#10b981' }}>
                {formatNumber ? formatNumber(totals.totalPurchased) : totals.totalPurchased.toLocaleString()}
              </strong>
            </div>
            {/*
              **Le chiffre d'affaires du site, et non la somme des articles.**

              `itemRevenue` vaut prix × quantité : avant les remises posées au niveau de la
              commande, et sans retrancher les remboursements. Sur une boutique qui pratique des
              codes promo, sa somme **dépasse** le montant encaissé, et ce module annonçait donc
              un chiffre d'affaires supérieur à celui du reste du tableau de bord — le Parcours
              d'achat, lui, lit la valeur de l'événement `purchase` et tombait juste.

              On affiche ici la même mesure que partout ailleurs ; l'écart est annoncé sous
              l'en-tête plutôt que tu. Les colonnes gardent `itemRevenue`, qui est bien le
              chiffre d'affaires de chaque article.
            */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <DollarSign size={16} style={{ color: '#6b7280' }} />
              <span style={{ color: '#6b7280' }}>Chiffre d'affaires:</span>
              <strong style={{ color: '#059669' }}>
                {(() => {
                  const montant = totals.chiffreAffairesSite?.revenue ?? totals.totalRevenue;
                  return formatNumber ? formatNumber(montant) : montant.toLocaleString();
                })()}€
              </strong>
            </div>
          </div>
          {toggleClientModuleVisibility && (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Achats e-commerce"
            />
          )}
        </div>
      </div>

      {/*
        L'écart entre le prix des articles et le montant encaissé, dit plutôt que tu.

        Il n'y en a pas toujours : une boutique sans remise au niveau de la commande et sans
        remboursement voit les deux coïncider. Le seuil de 1 % évite d'afficher une mention
        pour un arrondi de centimes.

        Le sens de l'écart se lit dans la phrase : les articles au-dessus, c'est une remise ou
        un remboursement ; en dessous, des frais facturés hors article — port, emballage.
      */}
      {(() => {
        const site = totals.chiffreAffairesSite?.revenue;
        const articles = totals.totalRevenue;
        if (!site || !articles) return null;
        const ecart = articles - site;
        if (Math.abs(ecart) / site <= 0.01) return null;

        const montant = (v) => (formatNumber ? formatNumber(Math.abs(v)) : Math.abs(v).toLocaleString());
        return (
          <div style={{
            padding: '10px 20px',
            borderBottom: '1px solid #e5e7eb',
            backgroundColor: '#fffbeb',
            fontSize: '0.8rem',
            color: '#92400e',
            lineHeight: 1.5
          }}>
            Le prix des articles totalise <strong>{montant(articles)}€</strong>, soit{' '}
            <strong>{montant(ecart)}€ {ecart > 0 ? 'de plus' : 'de moins'}</strong> que le chiffre
            d'affaires encaissé. Google Analytics compte le prix d'un article avant les remises
            posées au niveau de la commande et sans retrancher les remboursements
            {ecart < 0 ? ", et les frais facturés hors article — port, emballage — n'y figurent pas" : ''}.
            Les lignes ci-dessous portent donc le prix des articles, pas leur part du montant encaissé.
          </div>
        );
      })()}

      {/* Tableau */}
      <div style={{ overflowX: 'auto' }}>
        {/* La classe n'a aucune règle propre : elle sert à faire entrer ce tableau, construit
            entièrement en styles en ligne, dans les règles communes de `table[class*="-table"]`
            — largeur de colonne uniforme, en-têtes qui passent à la ligne, valeurs centrées. */}
        <table className="ga4-products-table" style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.9rem'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb' }}>
              <th
                onClick={() => handleSort('itemName')}
                style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {groupBy === 'itemCategory' ? 'Catégorie' : 'Nom de l\'article'}
                  <SortIcon columnKey="itemName" />
                </div>
              </th>
              <th
                onClick={() => handleSort('viewed')}
                style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  <Eye size={14} />
                  Articles consultés
                  <SortIcon columnKey="viewed" />
                </div>
              </th>
              <th
                onClick={() => handleSort('addedToCart')}
                style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  <ShoppingCart size={14} />
                  Ajoutés au panier
                  <SortIcon columnKey="addedToCart" />
                </div>
              </th>
              <th
                onClick={() => handleSort('purchased')}
                style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  <CreditCard size={14} />
                  Articles achetés
                  <SortIcon columnKey="purchased" />
                </div>
              </th>
              <th
                onClick={() => handleSort('revenue')}
                style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#374151',
                  borderBottom: '1px solid #e5e7eb',
                  cursor: 'pointer',
                  userSelect: 'none'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                  <DollarSign size={14} />
                  Chiffre d'affaires
                  <SortIcon columnKey="revenue" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedProducts.map((product, index) => (
              <tr
                key={product.itemName}
                style={{
                  backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                  transition: 'background-color 0.15s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb'}
              >
                <td style={{
                  padding: '12px 16px',
                  borderBottom: '1px solid #e5e7eb',
                  color: '#1f2937',
                  fontWeight: '500',
                  maxWidth: '300px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {product.itemName}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  borderBottom: '1px solid #e5e7eb',
                  color: '#4b5563'
                }}>
                  {formatNumber ? formatNumber(product.viewed) : product.viewed.toLocaleString()}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  borderBottom: '1px solid #e5e7eb',
                  color: '#4b5563'
                }}>
                  <span>{formatNumber ? formatNumber(product.addedToCart) : product.addedToCart.toLocaleString()}</span>
                  {product.viewToCartRate > 0 && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '0.8em',
                      color: product.viewToCartRate >= 10 ? '#059669' : '#d97706',
                      backgroundColor: product.viewToCartRate >= 10 ? '#d1fae5' : '#fef3c7',
                      padding: '2px 6px',
                      borderRadius: '10px'
                    }}>
                      {product.viewToCartRate}%
                    </span>
                  )}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  borderBottom: '1px solid #e5e7eb',
                  color: '#10b981',
                  fontWeight: '500'
                }}>
                  <span>{formatNumber ? formatNumber(product.purchased) : product.purchased.toLocaleString()}</span>
                  {product.cartToPurchaseRate > 0 && (
                    <span style={{
                      marginLeft: '8px',
                      fontSize: '0.8em',
                      color: product.cartToPurchaseRate >= 50 ? '#059669' : product.cartToPurchaseRate >= 20 ? '#d97706' : '#dc2626',
                      backgroundColor: product.cartToPurchaseRate >= 50 ? '#d1fae5' : product.cartToPurchaseRate >= 20 ? '#fef3c7' : '#fee2e2',
                      padding: '2px 6px',
                      borderRadius: '10px'
                    }}>
                      {product.cartToPurchaseRate}%
                    </span>
                  )}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  borderBottom: '1px solid #e5e7eb',
                  color: '#059669',
                  fontWeight: '600'
                }}>
                  {product.revenue > 0 ? (
                    `${formatNumber ? formatNumber(product.revenue) : product.revenue.toLocaleString()}€`
                  ) : (
                    <span style={{ color: '#9ca3af' }}>-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer avec pagination */}
      <div style={{
        padding: '12px 16px',
        backgroundColor: '#f9fafb',
        borderTop: '1px solid #e5e7eb',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
          Affichage {startIndex + 1}-{endIndex} sur {totalProducts} {groupBy === 'itemCategory' ? (totalProducts > 1 ? 'catégories' : 'catégorie') : (totalProducts > 1 ? 'articles' : 'article')}
        </div>

        {/* Contrôles de pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : '#ffffff',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s'
              }}
            >
              ← Précédent
            </button>
            <span style={{ fontSize: '0.85em', color: '#6b7280', padding: '0 8px' }}>
              Page {currentPage} sur {totalPages}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                fontSize: '0.85em',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : '#ffffff',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                transition: 'all 0.15s'
              }}
            >
              Suivant →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GA4EcommerceProducts;
